import { useMemo } from 'react';
import { Field, FieldError } from '@common-fe/common-fe';

import { TRUE_KEY, YES_KEY } from '@/common/constants';
import { useValidationUtils } from '@/utils/hooks/useValidationUtils';

import { useHealthPlanNameStore } from '../../BasicProperties/HealthPlanName/stores';
import {
  AUTO_FUNDING_PREFIX,
  CATEGORY_SPEND_LIMIT_PREFIX,
  CUSTOM_TRIGGER_AMOUNT_PREFIX,
  SPEND_LIMIT_PREFIX,
  toAutoFundingKey,
  toCategorySpendLimitKey,
  toSpendLimitKey,
} from '../../CoverageLevels/CoverageLevels.constants';
import { useCoverageLevelsFields, useCoverageLevelsState } from '../../CoverageLevels/hooks';
import {
  getAccountFundingItemsValidation,
  getCategorySpendLimitValidation,
  getPerPersonSpendLimitValidation,
} from '../../CoverageLevels/hooks/coverageLevelsFields.helpers';

interface Params {
  isTemplateMode?: boolean;
  isActiveMode?: boolean;
}
interface CoverageMap {
  [key: string]: string | number | boolean;
}
export default (params: Params) => {
  const { getValidateFields, getErrors } = useValidationUtils();
  const coverageLevelsState = useCoverageLevelsState();
  const perPersonSpendLimit = useHealthPlanNameStore((state) => state.state.perPersonSpendLimit);
  const perCategorySpendLimit = useHealthPlanNameStore((state) => state.state.perCategorySpendLimit);
  const autocreateCdhEnrollment = useHealthPlanNameStore((state) => state.state.autocreateCdhEnrollment);
  const plans = useHealthPlanNameStore((state) => state.state.plans);
  const hasLinkedPlans = useMemo(
    () => plans?.some((plan): plan is string => typeof plan === 'string' && !!plan) ?? false,
    [plans]
  );
  const isAutocreateEnabled = hasLinkedPlans && autocreateCdhEnrollment === TRUE_KEY;
  const isCategorySpendLimitEnabled = hasLinkedPlans && perCategorySpendLimit === YES_KEY;
  const coverageLevelsFields = useCoverageLevelsFields('', {
    isTemplateMode: params.isTemplateMode,
    isActiveMode: params.isActiveMode,
  });

  const coveragesErrors = useMemo(() => {
    const { coverageLevels } = coverageLevelsState;
    let allErrors: FieldError = {};

    coverageLevels.forEach((state) => {
      const planFields: Field[] = [];

      if (state?.accountFundingConfigs) {
        state.accountFundingConfigs.forEach((config) => {
          if (isAutocreateEnabled) {
            planFields.push(getAccountFundingItemsValidation(config));
          }
          // Only validate per person spend limit when it's enabled
          if (hasLinkedPlans && perPersonSpendLimit === YES_KEY) {
            planFields.push(getPerPersonSpendLimitValidation(config));
          }
        });
      }

      if (isCategorySpendLimitEnabled && state?.categorySpendLimits) {
        state.categorySpendLimits.forEach((limit) => {
          planFields.push(getCategorySpendLimitValidation(limit));
        });
      }

      const formatedState = {
        ...state,
        ...(state?.customTriggerAmounts || {}),
      } as unknown as CoverageMap;
      if (state?.accountFundingConfigs) {
        state?.accountFundingConfigs.forEach((item) => {
          if (isAutocreateEnabled) {
            const key = toAutoFundingKey(item.planId);
            if (!formatedState[key]) {
              formatedState[key] = item.autoEnrollmentAmount || '';
            }
          }
          if (hasLinkedPlans && perPersonSpendLimit === YES_KEY) {
            const key = toSpendLimitKey(item.planId);
            if (!formatedState[key]) {
              formatedState[key] = item.perPersonSpendLimit || '';
            }
          }
        });
      }

      if (isCategorySpendLimitEnabled && state?.categorySpendLimits) {
        state.categorySpendLimits.forEach((limit) => {
          const key = toCategorySpendLimitKey(limit.planId || '', limit.categoryType);
          if (!formatedState[key]) {
            formatedState[key] = limit.categorySpendLimit || '';
          }
        });
      }

      const validators = getValidateFields([...coverageLevelsFields, ...planFields]);
      const errors = getErrors(formatedState, validators);
      Object.keys(errors).forEach((key) => {
        if (
          key.startsWith(AUTO_FUNDING_PREFIX) ||
          key.startsWith(SPEND_LIMIT_PREFIX) ||
          key.startsWith(CATEGORY_SPEND_LIMIT_PREFIX)
        ) {
          const item = errors[key];
          delete errors[key];
          errors.coverageAccountFunding = item;
        }
        if (key.startsWith(CUSTOM_TRIGGER_AMOUNT_PREFIX)) {
          const item = errors[key];
          delete errors[key];
          errors.coverageTriggers = item;
        }
      });
      allErrors = {
        ...allErrors,
        ...errors,
      };
    });
    return allErrors;
  }, [
    coverageLevelsFields,
    coverageLevelsState,
    getErrors,
    getValidateFields,
    perPersonSpendLimit,
    isAutocreateEnabled,
    isCategorySpendLimitEnabled,
    hasLinkedPlans,
  ]);
  return coveragesErrors;
};
